import { useParams, useRouter } from '@tanstack/react-router';
import {
	AddressDTO,
	ConditionDTOType,
	ConditionKey,
	PartySubscriptionConditionDto,
	PriceRequestDto,
	PriceResponseDto,
	ShippingLocationRequestDto,
	useGetSubscriptionByProductSuspense,
	useSavePriceRequest,
} from '@uturn/api/v1';
import { Page } from '@uturn/portal/components';
import { MetaDataContext } from '@uturn/portal/context';
import {
	additionalRequirementOptions,
	LocationField,
	PriceCalculatorResult,
	PriceRequestForm,
	PriceRequestFormFields,
	ProtectedPriceCalculatorResultById,
	RemainingRequestsBanner,
	RouteMap,
} from '@uturn/portal/modules/price-calculator/components';
import { PriceCalculatorContext } from '@uturn/portal/modules/price-calculator/context/price-calculator-context';
import { defaultValues } from '@uturn/portal/modules/price-calculator/schema';
import { usePriceCalculatorStore } from '@uturn/portal/store/price-calculator';
import { getApiErrorMessage, getApiErrorStatus } from '@uturn/portal/utils';
import { sonner } from '@uturn/ui-kit';
import { AnimatePresence } from 'framer-motion';
import type { TFunction } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const getEnabledFeatures = (
	t: TFunction<'translation', undefined>,
	conditions: PartySubscriptionConditionDto[] | undefined,
) => {
	if (!conditions) {
		return [];
	}

	const enabledFeatures =
		conditions
			.filter(
				(item) =>
					item.type === ConditionDTOType.BOOLEAN && item.value === 'true',
			)
			.map((item) => item.key) ?? [];

	if (!enabledFeatures.length) {
		return [];
	}

	const result = additionalRequirementOptions(t)
		.filter(
			(item) =>
				item.conditionKey && enabledFeatures.includes(item.conditionKey),
		)
		.map((item) => item.conditionKey as ConditionKey); // Guarantied by Type Predicate Inference

	return result;
};

export function ProtectedPriceCalculator() {
	const { t } = useTranslation();
	const { uuid } = useParams({ from: '/_protected/calculator/{-$uuid}' });
	const router = useRouter();

	const { metadata } = useContext(MetaDataContext);

	const { resetRouteInformation } = usePriceCalculatorStore();

	const savePriceRequest = useSavePriceRequest();
	const { data: partySubscription } = useGetSubscriptionByProductSuspense(1, {
		query: {
			gcTime: 0,
			select: (data) => data.data,
		},
	});

	const [priceRequest, setPriceRequest] =
		useState<PriceRequestFormFields>(defaultValues);
	const [priceResponse, setPriceResponse] = useState<PriceResponseDto | null>(
		null,
	);
	const [locations, setLocations] = useState<AddressDTO[]>([]);
	const [remainingRequests, setRemainingRequests] = useState<number>(
		partySubscription.activeSubscription?.remainingRequests ?? 0,
	);

	useEffect(() => {
		resetRouteInformation();
	}, []);

	useEffect(() => {
		if (uuid === undefined) {
			setPriceResponse(null);
		}
	}, [uuid]);

	useEffect(() => {
		if (
			remainingRequests === 0 &&
			priceResponse === null &&
			uuid === undefined
		) {
			router.navigate({ to: '/calculator/subscriptions' });
		}
	}, [remainingRequests]);

	const onSubmit = (request: PriceRequestFormFields) => {
		const email = metadata?.user?.username ?? '';
		const dtoLocations: ShippingLocationRequestDto[] = request.locations.map(
			(location: LocationField) => ({
				id: location.id ?? undefined,
				...(location.googlePlaceId && {
					googlePlaceId: location.googlePlaceId,
				}),
			}),
		);
		const submitData: PriceRequestDto = {
			transportType: request.transportType,
			containerSize: request.containerSize,
			additionalRequirement: request.additionalRequirement,
			locations: dtoLocations,
			email,
		};

		setPriceRequest(submitData);

		savePriceRequest.mutate(
			{ data: submitData },
			{
				onSuccess: (response) => {
					setPriceResponse(response.data);
					// Only set remaining if it's not unlimited
					if (remainingRequests !== -1) {
						setRemainingRequests(remainingRequests - 1);
					}
					router.navigate({
						to: '/calculator/{-$uuid}',
						replace: true,
						params: { uuid: response.data.requestId },
					});
				},
				onError: (error) => {
					const status = getApiErrorStatus(error);
					const message = getApiErrorMessage(error);
					if (status === 500) {
						sonner.error(message);
						return;
					}
					if (status === 400 || status === 404) {
						// TODO: Replace this as it doesn't do anything atm.
						// handleSystemNotification({
						// 	color: 'danger',
						// 	title: t(
						// 		'general.error-message',
						// 		'Something went wrong, {{ errorMessage }}',
						// 		{ errorMessage: message },
						// 	),
						// 	icon: faXmark,
						// });
						sonner.error(message);
						return;
					}
					sonner.error(
						t(
							'pages.price-calculator.form.hint.error.title',
							'Subscription issue?',
						),
						{
							description: message,
						},
					);
					// TODO: Should we really navigate to subscriptions here?
					router.navigate({
						to: '/calculator/subscriptions',
						replace: true,
					});
				},
			},
		);
	};

	const onReset = () => {
		if (remainingRequests === 0) {
			router.navigate({
				to: '/calculator/subscriptions',
				replace: true,
			});
		} else {
			router.navigate({
				to: '/calculator/{-$uuid}',
				replace: true,
			});
		}
		setPriceResponse(null);
		setPriceRequest(defaultValues);
		setLocations([]);
	};

	return (
		<>
			<Helmet
				title={t('pages.price-calculator.title', 'Price Calculator').toString()}
			/>
			<Page
				title={t('pages.price-calculator.title', 'Price Calculator').toString()}
			>
				<div className="relative grid gap-8 md:grid-cols-[minmax(0,3fr),_minmax(0,2fr)]">
					<PriceCalculatorContext.Provider
						value={{
							locations,
							setLocations: (newLocations) => setLocations(newLocations),
						}}
					>
						<div className="flex w-full flex-col gap-4">
							{remainingRequests !== -1 && partySubscription && (
								<RemainingRequestsBanner
									subscription={partySubscription}
									remainingRequests={remainingRequests}
								/>
							)}
							<AnimatePresence mode="wait">
								{priceResponse === null && uuid === undefined && (
									<PriceRequestForm
										additionalRequirementEnabledFeatures={getEnabledFeatures(
											t,
											partySubscription.activeSubscription?.conditions,
										)}
										defaultValues={priceRequest}
										onSubmit={onSubmit}
										onTripChange={setLocations}
									/>
								)}

								{priceResponse !== null && uuid !== undefined && (
									<PriceCalculatorResult
										locations={locations}
										priceRequest={priceRequest}
										priceResponse={priceResponse}
										onRequestEdit={() => {
											if (remainingRequests === 0) {
												router.navigate({
													to: '/calculator/subscriptions',
													replace: true,
												});
											} else {
												router.navigate({
													to: '/calculator/{-$uuid}',
													params: { uuid: undefined },
													replace: true,
												});
											}
											setPriceRequest((request) => {
												return {
													...request,
													locations,
												};
											});
											setPriceResponse(null);
										}}
										onRequestReset={onReset}
									/>
								)}

								{priceResponse === null && uuid !== undefined && (
									<ProtectedPriceCalculatorResultById />
								)}
							</AnimatePresence>
						</div>
					</PriceCalculatorContext.Provider>
					<div className="relative hidden md:block">
						<RouteMap
							locations={locations
								.filter(
									(location) => !!location.latitude && !!location.longitude,
								)
								.map((location) => ({
									lat: location.latitude as number, // Guarantied by Type Predicate Inference
									lng: location.longitude as number, // Guarantied by Type Predicate Inference
								}))}
						/>
					</div>
				</div>
			</Page>
		</>
	);
}
